import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'antd';
import { GlobalOutlined, UserOutlined } from '@ant-design/icons';
import { LangText } from '../LangText';
import { preFixImgUrl } from '@/utils';
import { useRootStore } from '@/contexts';
import { useNavigate } from 'react-router-dom';
import jsCookie from 'js-cookie';
import { useState, useEffect } from 'react';

export function Header() {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { userStore } = useRootStore()
  const { language } = i18n
  const isHomePage = location.pathname === '/';
  // 初始化时，如果在首页则默认为false（未滚动），否则为true（显示白色背景）
  const [isScrolled, setIsScrolled] = useState(!isHomePage);

  useEffect(() => {
    if (!isHomePage) {
      setIsScrolled(true);
      return;
    }

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 50);
    };

    // 初始化检查滚动位置
    handleScroll();
    
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHomePage]);
  const langText: string = {
    'zh_CN': '中文',
    'en_US': 'English',
    'ja_JP': '日本語'
  }[language as 'zh_CN' | 'en_US' | 'ja_JP']

  const handleLangChange = ({ key }: { key: string }) => {
    if (key === language) return;
    jsCookie.set('i18next', key);
    localStorage.setItem('i18next', key);
    window.location.reload();
  }

  const langsOptions = [
    {
      key: 'zh_CN',
      label: '中文',
      className: '!text-[16px]',
      onClick: handleLangChange
    },
    {
      key: 'en_US',
      label: 'English',
      className: '!text-[16px]',
      onClick: handleLangChange
    },
    {
      key: 'ja_JP',
      label: '日本語',
      className: '!text-[16px]',
      onClick: handleLangChange
    },
  ]

  const navigate = useNavigate()
  
  // 根据是否在首页和是否滚动来决定header样式
  const isTransparent = isHomePage && !isScrolled;
  
  const headerClassName = isTransparent
    ? 'sticky top-0 z-40 transition-all duration-300 !bg-transparent' 
    : 'bg-white sticky top-0 z-40 transition-all duration-300';
  
  const textClassName = isTransparent
    ? 'text-white'
    : 'text-black';

  return (
    <header 
      className={headerClassName}
    >
      <div className='flex justify-between items-center px-10 mx-auto h-20 max-w-8xl'>
        <Link to='/'>
          <img 
            alt='logo'
            className='h-10'
            src={isTransparent
              ? preFixImgUrl('181bb0761a95a0853af1c9d46e0d34b8.png')
              : preFixImgUrl('04d23d306013bdcfb3fc8d2df354973c.png')
            } 
          />
        </Link>
        <div className={`flex gap-10 items-center text-[16px] ${textClassName}`}>
          <Link to='/'>
            <LangText lngKey='8dbe8fad' className={`${location.pathname === '/' && (isHomePage && !isScrolled ? 'text-white' : 'text-[#5a52ff]')} ${isHomePage && !isScrolled ? 'hover:text-white/80' : 'hover:text-[#5a52ff]'}`}>{t('8dbe8fad')}
            </LangText>
          </Link>
          <Link to='/service-center'>
            <LangText lngKey='ea8f2288' className={`${location.pathname === '/service-center' && (isHomePage && !isScrolled ? 'text-white' : 'text-[#5a52ff]')} ${isHomePage && !isScrolled ? 'hover:text-white/80' : 'hover:text-[#5a52ff]'}`}>{t('ea8f2288')}
            </LangText>
          </Link>
          <Link to='/orders'>
            <LangText lngKey='220d4611' className={`${location.pathname === '/orders' && (isHomePage && !isScrolled ? 'text-white' : 'text-[#5a52ff]')} ${isHomePage && !isScrolled ? 'hover:text-white/80' : 'hover:text-[#5a52ff]'}`}>{t('220d4611')}
            </LangText>
          </Link>
          <Dropdown
            menu={{
              items: langsOptions,
            }}
            placement="bottomRight"
          >
            <button className={`flex gap-2 items-center focus:outline-none ${textClassName}`}>
              <GlobalOutlined className='text-[24px]' />
              <div className='flex gap-1 items-center'>
                {langText}
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                  <path d="M5.25 8.25L10 13l4.75-4.75" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                </svg>
              </div>
            </button>
          </Dropdown>
          {
            userStore.user ? (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'profile',
                      label: t('9cd7aa87'),
                      className: '!text-[16px]',
                      onClick: () => {
                        navigate('/profile')
                      }
                    },
                    {
                      key: 'logout',
                      label: t('4fae7f07'),
                      className: '!text-[16px]',
                      onClick: () => {
                        userStore.logOut()
                      }
                    },
                  ]
                }}
                placement="bottomRight"
              >
                <button className={`flex gap-3 items-center focus:outline-none ${textClassName}`}>
                  <UserOutlined className={`flex-shrink-0 text-[20px] ${textClassName}`} />
                  <div className='flex gap-1 items-center'>
                    <LangText lngKey='dd93e279' className=''>{t('dd93e279')}</LangText>
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                      <path d="M5.25 8.25L10 13l4.75-4.75" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    </svg>
                  </div>
                </button>
              </Dropdown>
            ) : (
              <Link to='/signin' className='rounded-[18px] bg-linear-to-r from-[#a75aff] to-[#4b51ff] h-9 leading-9 px-[22px]'>
                <LangText lngKey='0b9c26ed' className='text-white text-[16px]'>{t('0b9c26ed')}
                </LangText>
              </Link>
            )
          }
        </div>
      </div>
    </header>
  );
}

